/**
 * The auto-review sweep dialog (design-reference §4.17). Offered right after a
 * category is created from the reviewer: it re-classifies documents that were
 * parked as *Other* / low-confidence against the freshly-extended taxonomy, from
 * their stored OCR text — no re-scan. The affected count comes from the real
 * preview (`POST /v1/review/sweep` with `apply:false`), and re-previews when the
 * "include already-classified Other" scope changes; running it applies
 * (`apply:true`) and reports how many reclassify messages were enqueued.
 *
 * Note on fidelity: the mock's confidence-threshold slider and apply/suggest radio
 * are not backed by the real API — the sweep enqueues candidates and the
 * classification worker makes the apply-vs-suggest decision per document on
 * confidence (backend `services/sweep.py`). The one real caller-side lever is the
 * `include_classified_other` scope, surfaced here as a toggle.
 */
import { useEffect, useState } from 'react';

import type { SweepResponse } from '../../api/schema';
import { Button, Modal, Toggle } from '../../components';

import type { ReviewApi } from './reviewApi';

export interface SweepModalProps {
  open: boolean;
  api: ReviewApi;
  categoryId: string;
  categoryName: string;
  /** Affected count from the create-resolution preview (avoids a first round-trip). */
  initialAffected: number;
  onSkip: () => void;
  onComplete: (result: SweepResponse) => void;
  onError: (message: string) => void;
}

export function SweepModal({
  open,
  api,
  categoryId,
  categoryName,
  initialAffected,
  onSkip,
  onComplete,
  onError,
}: SweepModalProps): React.JSX.Element | null {
  const [includeOther, setIncludeOther] = useState(true);
  const [affected, setAffected] = useState(initialAffected);
  const [previewing, setPreviewing] = useState(false);
  const [running, setRunning] = useState(false);

  // Reset the projected count whenever a new category opens the dialog.
  useEffect(() => {
    if (open) {
      setAffected(initialAffected);
      setIncludeOther(true);
    }
  }, [open, initialAffected, categoryId]);

  if (!open) return null;

  const reprofile = (next: boolean): void => {
    setIncludeOther(next);
    setPreviewing(true);
    void (async () => {
      try {
        const res = await api.sweep({
          category_id: categoryId,
          apply: false,
          include_classified_other: next,
        });
        setAffected(res.affected ?? 0);
      } catch {
        // Keep the last known count; the run action re-validates server-side.
      } finally {
        setPreviewing(false);
      }
    })();
  };

  const run = (): void => {
    setRunning(true);
    void (async () => {
      try {
        const res = await api.sweep({
          category_id: categoryId,
          apply: true,
          include_classified_other: includeOther,
        });
        onComplete(res);
      } catch {
        onError('The sweep could not run — please try again.');
      } finally {
        setRunning(false);
      }
    })();
  };

  return (
    <Modal open={open} onClose={onSkip} title="Run an auto-review sweep?" width="lg" dismissible>
      <div className="ds-modal__body ds-sweep">
        <p className="ds-sweep__lead">
          “<strong>{categoryName}</strong>” is now in your taxonomy. I can re-classify similar
          documents from their stored OCR text — no re-scanning or re-OCR needed.
        </p>

        <div className="ds-sweep__count" aria-live="polite">
          <span className="ds-sweep__num" aria-hidden={previewing}>
            {previewing ? '…' : affected.toLocaleString('en-GB')}
          </span>
          <span className="ds-sweep__count-cap">
            {affected === 1 ? 'document matches' : 'documents match'} and would be re-classified as “
            {categoryName}”.
          </span>
        </div>

        <div className="ds-sweep__scope">
          <span>
            <span className="ds-sweep__scope-title">Include documents already filed as “Other”</span>
            <span className="ds-sweep__scope-sub">
              Widens the sweep beyond the review queue to previously-classified Other documents.
            </span>
          </span>
          <Toggle
            label="Include documents already filed as Other"
            checked={includeOther}
            onChange={reprofile}
            disabled={running}
          />
        </div>
      </div>

      <div className="ds-modal__footer">
        <Button variant="secondary" onClick={onSkip} disabled={running}>
          Skip for now
        </Button>
        <Button
          variant="primary"
          onClick={run}
          loading={running}
          loadingLabel="Sweeping…"
          disabled={previewing}
        >
          Run sweep
        </Button>
      </div>
    </Modal>
  );
}
